import {
    SELECTED_TAB_STORAGE_KEY,
} from '~spa/Pages/Homepage/constants';

const TOP_SECTION_WIDGETS = ['carousel_menu', 'podtatrze'];

/**
 * Generates an array of tab objects from the given widgets data.
 *
 * @param {Object} widgetsData - An object containing widget data, where keys represent widget identifiers.
 * @param {string} dataTestIdPrefix - A prefix string to be used for data-test-id attributes.
 * @returns {Array<Object>} An array of tab objects
 */
export const generateTabs = (widgetsData, dataTestIdPrefix) =>
    Object.entries(widgetsData)
        .filter(([key]) => key.startsWith('tab-')) // Filter keys that start with 'tab-'
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => ({
            slug: key,
            content: value.title,
            dataTestId: `${dataTestIdPrefix}${key}`,
            dataSpectateProduct: `${dataTestIdPrefix}${key}`,
        }));

/**
 * Generates a list of widget keys that correspond to the user's favorite sports.
 *
 * @param {Object} widgetsMapping - An object mapping widget keys to sport IDs.
 * @param {Array<number>} favouriteSportIds - An array of favorite sport IDs to match against.
 * @returns {Array<string>} An array of widget keys that match the favorite sport IDs.
 */
export const generateFavouriteSportsWidgetsList = (widgetsMapping, favouriteSportIds) => {
    if (favouriteSportIds.length === 0) {

        return [];
    }

    return Object.keys(widgetsMapping).filter(key => favouriteSportIds.includes(widgetsMapping[key]));
};

/**
 * Orders the widgets list by placing favourite sports widgets at the beginning.
 *
 * @param {Array<string>} widgetsList - An array of widgets to be ordered.
 * @param {Array<string>} favouriteSportsWidgets - An array of favourite sports widgets.
 * @returns {Array<string>} The ordered array of widgets.
 */
export const orderWithFavouriteWidgets = (widgetsList, favouriteSportsWidgets) => {
    if (favouriteSportsWidgets.length === 0) {

        return widgetsList;
    }

    const favouriteWidgets = [];
    const otherWidgets = [];

    widgetsList.forEach(widgetSlug => {
        if (favouriteSportsWidgets.includes(widgetSlug)) {
            favouriteWidgets.push(widgetSlug);
        } else {
            otherWidgets.push(widgetSlug);
        }
    });

    return [...favouriteWidgets, ...otherWidgets];
};

/**
 * If we have a rollback and the client is still new working with an old contract containing the
 * 'fixed' key, we will remap the widgets into 'tab-1', 'tab-2' and 'top-section'.
 *
 * @param {Object} widgetsList - The widgets list.
 * @param {string} topSectionIdentifier - The key for the top section in the widgets list.
 * @returns {{widgetsList: Object, topSectionWidgets: Array}} The mapped widgets list and top-section widgets.
 */
export const formatWidgetList = (widgetsList, topSectionIdentifier) => {
    // Older contract will have 'fixed' key
    const hasFixedKey = widgetsList && Object.hasOwn(widgetsList, 'fixed');

    if (!hasFixedKey) {
        const topSectionWidgets = widgetsList?.[topSectionIdentifier]?.widgets || [];

        return {
            widgetsList,
            topSectionWidgets,
        };
    }

    const fixedWidgets = Object.values(widgetsList.fixed);
    const topSectionFromFixed = fixedWidgets.filter((widget) => TOP_SECTION_WIDGETS.includes(widget));
    const tab1FromFixed = fixedWidgets.filter((widget) => !TOP_SECTION_WIDGETS.includes(widget));

    const mappedWidgetsList = {
        ...(tab1FromFixed.length ? { 'tab-1': { title: 'Highlights', widgets: tab1FromFixed }} : {}),
        ...(widgetsList?.fixed_sports ? { 'tab-2': { title: 'Top Sports', widgets: Object.values(widgetsList.fixed_sports) }} : {}),
        ...(widgetsList?.widgets_mapping ? { 'widgets-mapping': widgetsList.widgets_mapping } : {}),
    };

    return {
        widgetsList: mappedWidgetsList,
        topSectionWidgets: topSectionFromFixed,
    };
};

/**
 * Gets the stored homepage tab key from local storage.
 * Returns an empty string when the key is missing or storage access fails.
 *
 * @returns {string} The stored homepage tab key, or an empty string.
 */
export const getHomepageStoredTab = () => {

    try {

        return localStorage.getItem(SELECTED_TAB_STORAGE_KEY) || '';
    } catch (error) {

        return '';
    }
};
